import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Check, MapPin } from 'lucide-react'

const SPECIALTIES = ['beginner', 'intermediate', 'advanced', 'kids', 'private', 'adaptive']
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export default function InstructorOnboarding({ onComplete }) {
  const { user, profile } = useAuth()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [schools, setSchools] = useState([])
  const [submitError, setSubmitError] = useState('')
  const [form, setForm] = useState({
    school_id: '', bio: '', specialties: [], certifications: '',
    availability: [], start_time: '08:00', end_time: '17:00',
  })

  useEffect(() => {
    supabase.from('schools').select('*').then(({ data }) => setSchools(data || []))
  }, [])

  const toggle = (key, value) => setForm(f => ({
    ...f, [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value]
  }))
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async () => {
    setSubmitError('')

    if (!form.school_id) {
      setSubmitError('Please select a school before submitting.')
      setStep(1)
      return
    }

    if (!user?.id) {
      setSubmitError('You must be logged in to submit.')
      return
    }

    setSaving(true)

    // Check if instructor record already exists for this user
    const { data: existing } = await supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', user.id)
      .single()

    let instructor = existing
    let insertError = null

    if (!existing) {
      const { data: newInstructor, error } = await supabase
        .from('instructors')
        .insert({
          profile_id: user.id,
          school_id: form.school_id,
          specialties: form.specialties,
          certifications: form.certifications
            ? form.certifications.split(',').map(c => c.trim()).filter(Boolean)
            : [],
          bio: form.bio,
          rating: 5.0,
          is_active: true,
          approved: false,
          hourly_rate: 0,
        })
        .select()
        .single()

      instructor = newInstructor
      insertError = error
    } else {
      // Update existing record with new school
      const { error } = await supabase
        .from('instructors')
        .update({
          school_id: form.school_id,
          specialties: form.specialties,
          certifications: form.certifications
            ? form.certifications.split(',').map(c => c.trim()).filter(Boolean)
            : [],
          bio: form.bio,
          approved: false,
        })
        .eq('id', existing.id)
      insertError = error
    }

    if (insertError) {
      setSubmitError('Error saving instructor: ' + insertError.message)
      setSaving(false)
      return
    }

    // Save availability
    if (instructor && form.availability.length > 0) {
      const dayMap = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 0 }
      const rows = form.availability.map(day => ({
        instructor_id: instructor.id,
        day_of_week: dayMap[day],
        start_time: form.start_time,
        end_time: form.end_time,
        is_recurring: true,
      }))
      await supabase.from('availability').insert(rows)
    }

    setSaving(false)
    setStep(4)
  }

  const steps = [
    { n: 1, label: 'Your school' },
    { n: 2, label: 'Specialties' },
    { n: 3, label: 'Availability' },
    { n: 4, label: 'Done' },
  ]
  const selectedSchool = schools.find(s => s.id === form.school_id)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ width: 52, height: 52, background: 'var(--coral)', borderRadius: '50% 50% 50% 0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, transform: 'rotate(-45deg)', marginBottom: '1rem' }}>
            <span style={{ transform: 'rotate(45deg)', display: 'block' }}>🌊</span>
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: 4 }}>
            Welcome, {profile?.full_name?.split(' ')[0]}!
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem' }}>Set up your instructor profile</p>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          {steps.map((s, i) => (
            <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem', background: step > s.n ? 'var(--teal)' : step === s.n ? 'var(--coral)' : 'rgba(255,255,255,0.1)', color: step >= s.n ? 'white' : 'rgba(255,255,255,0.3)', transition: 'all 0.3s' }}>
                  {step > s.n ? <Check size={14} /> : s.n}
                </div>
                <div style={{ fontSize: '0.62rem', color: step === s.n ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)', marginTop: 3, whiteSpace: 'nowrap' }}>{s.label}</div>
              </div>
              {i < steps.length - 1 && (
                <div style={{ flex: 1, height: 1.5, background: step > s.n ? 'var(--teal)' : 'rgba(255,255,255,0.1)', marginTop: -14, transition: 'background 0.3s' }} />
              )}
            </div>
          ))}
        </div>

        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '2rem', backdropFilter: 'blur(12px)' }}>

          {/* Error banner */}
          {submitError && (
            <div style={{ padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', background: 'rgba(232,97,74,0.12)', border: '1px solid rgba(232,97,74,0.3)', color: '#E8614A', fontSize: '0.85rem' }}>
              {submitError}
            </div>
          )}

          {/* Step 1 — School */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'white', marginBottom: 4 }}>Which surf school do you work at?</h2>
              {schools.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem', background: 'rgba(255,255,255,0.04)', borderRadius: 10 }}>
                  No schools found yet. Ask your manager to set up the school first.
                </div>
              ) : schools.map(school => (
                <button key={school.id} onClick={() => setForm(f => ({ ...f, school_id: school.id }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '1rem', borderRadius: 12, border: '1.5px solid', borderColor: form.school_id === school.id ? 'var(--teal)' : 'rgba(255,255,255,0.12)', background: form.school_id === school.id ? 'rgba(14,170,164,0.12)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', fontFamily: 'DM Sans, sans-serif' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(14,170,164,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🏄</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'white' }}>{school.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <MapPin size={10} /> {school.location}
                    </div>
                  </div>
                  {form.school_id === school.id && (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={14} color="white" />
                    </div>
                  )}
                </button>
              ))}
              <div className="form-group" style={{ marginTop: 4 }}>
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>Certifications (optional)</label>
                <input className="form-control" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }} placeholder="e.g. ISA Level 1, CPR/First Aid" value={form.certifications} onChange={set('certifications')} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>Bio (optional)</label>
                <textarea className="form-control" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', resize: 'none' }} rows={2} placeholder="Tell students about yourself..." value={form.bio} onChange={set('bio')} />
              </div>
            </div>
          )}

          {/* Step 2 — Specialties */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'white', marginBottom: 4 }}>What do you teach?</h2>
              <div>
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 8 }}>Lesson Specialties</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {SPECIALTIES.map(s => (
                    <button key={s} onClick={() => toggle('specialties', s)}
                      style={{ padding: '6px 14px', borderRadius: 50, border: '1.5px solid', borderColor: form.specialties.includes(s) ? 'var(--teal)' : 'rgba(255,255,255,0.15)', background: form.specialties.includes(s) ? 'rgba(14,170,164,0.2)' : 'transparent', color: form.specialties.includes(s) ? 'var(--teal)' : 'rgba(255,255,255,0.5)', fontSize: '0.825rem', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize', transition: 'all 0.2s', fontFamily: 'DM Sans, sans-serif' }}>
                      {form.specialties.includes(s) && '✓ '}{s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Availability */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', color: 'white', marginBottom: 4 }}>When are you available?</h2>
              <div>
                <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 8 }}>Days You Work</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {DAYS.map(day => (
                    <button key={day} onClick={() => toggle('availability', day)}
                      style={{ padding: '6px 14px', borderRadius: 50, border: '1.5px solid', borderColor: form.availability.includes(day) ? 'var(--teal)' : 'rgba(255,255,255,0.15)', background: form.availability.includes(day) ? 'rgba(14,170,164,0.2)' : 'transparent', color: form.availability.includes(day) ? 'var(--teal)' : 'rgba(255,255,255,0.5)', fontSize: '0.825rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'DM Sans, sans-serif' }}>
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>Start Time</label>
                  <input className="form-control" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }} type="time" value={form.start_time} onChange={set('start_time')} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ color: 'rgba(255,255,255,0.5)' }}>End Time</label>
                  <input className="form-control" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white' }} type="time" value={form.end_time} onChange={set('end_time')} />
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Done */}
          {step === 4 && (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: 48, marginBottom: '1rem' }}>⏳</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', color: 'white', marginBottom: '0.75rem' }}>Application submitted!</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                Your manager at <strong style={{ color: 'white' }}>{selectedSchool?.name}</strong> will review and approve your profile.
              </p>
              <div style={{ background: 'rgba(212,168,75,0.08)', border: '1px solid rgba(212,168,75,0.2)', borderRadius: 10, padding: '1rem', textAlign: 'left', fontSize: '0.825rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8 }}>
                <div>✅ School: <strong style={{ color: 'white' }}>{selectedSchool?.name}</strong></div>
                <div>✅ Specialties: {form.specialties.join(', ') || 'none'}</div>
                <div>⏳ Waiting for admin approval</div>
                <div>💰 Your pay rate will be set by your manager</div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', gap: '0.75rem' }}>
            {step > 1 && step < 4
              ? <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)} style={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)' }}>← Back</button>
              : <div />
            }
            {step === 1 && (
              <button className="btn btn-primary" disabled={!form.school_id} onClick={() => setStep(2)}>Next →</button>
            )}
            {step === 2 && (
              <button className="btn btn-primary" onClick={() => setStep(3)}>Next →</button>
            )}
            {step === 3 && (
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Submitting...' : 'Submit Application →'}
              </button>
            )}
            {step === 4 && (
              <button className="btn btn-primary" onClick={onComplete}>Go to Dashboard →</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
